/*
 * Version history for one article.
 *
 * What the data model makes possible, and what this therefore has to express:
 *
 *   - Revisions are append-only and immutable. There is no "edit revision 3"
 *     anywhere in this UI, because kb.TR_ArticleRevision_Immutable would reject it
 *     and, more to the point, an editable history is not a history.
 *   - At most one revision is the one readers get, marked here as "live". That is
 *     UX_ArticleRevision_Current, a unique index filtered on IsCurrent = 1.
 *     Promoting a different one is a single operation and takes effect immediately.
 *   - "Restore" appends a copy rather than moving the pointer backwards, and the
 *     button says so, because the two would leave very different audit trails.
 *   - A revision above the live one is a draft. That is the state the filtered
 *     unique index exists to permit, and it is called out rather than left to be
 *     inferred from the numbers.
 *
 * The diff is computed in the browser from two snapshots, since the database stores
 * whole revisions rather than deltas.
 *
 * A release note's history is this panel too (S42, 2026-10-12): the same
 * append-only, one-live, copy-forward rules hold for kb.ReleaseNoteRevision. The
 * four calls come through a `revisions` adapter instead of the article id, and
 * the rows it returns are the article summary without its articleId - the only
 * field of the summary that names an entity, and one this panel never read.
 *
 * Its words come from the chosen language's dictionary, `t.admin.articles` (S48).
 */

import { useMemo, useState } from 'react';
import type { AdminRevisionSummary } from '@accotool-kb/shared';
import { collapseUnchanged, diffLines } from '@accotool-kb/shared';
import { useAdminApi } from '../../auth/AuthProvider.js';
import type { AdminApi } from '../../api/adminApi.js';
import { useAction, useAsync } from '../../hooks/useAsync.js';
import { DateTime, ErrorBanner, Loading, Modal } from '../../components/ui.js';
import { useMessages } from '../../i18n/language.js';
import { richText } from '../../i18n/richText.js';

/** One row of the history: a revision summary, whichever entity it belongs to. */
export type RevisionRow = Omit<AdminRevisionSummary, 'articleId'>;

/** A revision with its text, for the viewer and the diff. */
export type RevisionWithContent = RevisionRow & { content: string };

/**
 * The four history calls for one entity. `key` names the entity, so the list is
 * fetched again when the panel is pointed at a different one - and only then, not
 * whenever a render rebuilds the adapter.
 */
export interface RevisionHistorySource {
  key: string;
  list(): Promise<RevisionRow[]>;
  get(revisionNumber: number): Promise<RevisionWithContent>;
  /** Makes this revision the one readers get. */
  promote(revisionNumber: number): Promise<unknown>;
  /** Appends a copy of this revision, published or as a draft. */
  restore(revisionNumber: number, publish: boolean): Promise<unknown>;
}

/** An article's history, through the article calls. */
export function articleRevisionSource(api: AdminApi, articleId: string): RevisionHistorySource {
  return {
    key: `article:${articleId}`,
    list: () => api.listRevisions(articleId),
    get: (revisionNumber) => api.getRevision(articleId, revisionNumber),
    promote: (revisionNumber) => api.promoteRevision(articleId, revisionNumber),
    restore: (revisionNumber, publish) => api.restoreRevision(articleId, revisionNumber, publish),
  };
}

/** A release note's history, through the release-note calls. */
export function releaseNoteRevisionSource(
  api: AdminApi,
  releaseNoteId: string,
): RevisionHistorySource {
  return {
    key: `release-note:${releaseNoteId}`,
    list: () => api.listReleaseNoteRevisions(releaseNoteId),
    get: (revisionNumber) => api.getReleaseNoteRevision(releaseNoteId, revisionNumber),
    promote: (revisionNumber) => api.promoteReleaseNoteRevision(releaseNoteId, revisionNumber),
    restore: (revisionNumber, publish) =>
      api.restoreReleaseNoteRevision(releaseNoteId, revisionNumber, publish),
  };
}

/** An article by id (the shorthand), or any entity by adapter. Exactly one. */
type HistoryOwner =
  | { articleId: string; revisions?: undefined }
  | { revisions: RevisionHistorySource; articleId?: undefined };

export function RevisionHistoryPanel({
  articleId,
  revisions: revisionsSource,
  currentRevisionNumber,
  onChanged,
  reloadKey,
}: HistoryOwner & {
  currentRevisionNumber: number | null;
  onChanged: () => void;
  reloadKey: number;
}): JSX.Element {
  const t = useMessages();
  const shared = t.admin.articles.shared;
  const words = t.admin.articles.history;
  const api = useAdminApi();
  const action = useAction();
  const [comparing, setComparing] = useState<RevisionRow | null>(null);
  const [viewing, setViewing] = useState<RevisionRow | null>(null);

  const source = useMemo(
    () => revisionsSource ?? articleRevisionSource(api, articleId as string),
    [revisionsSource, api, articleId],
  );

  // useAsync reads the loader through a ref, so the newest adapter is the one
  // called; the key is what decides when to call it again.
  const state = useAsync(() => source.list(), [source.key, reloadKey]);

  const promote = async (revisionNumber: number) => {
    const result = await action.run(() => source.promote(revisionNumber));
    if (result) {
      state.reload();
      onChanged();
    }
  };

  const restore = async (revisionNumber: number) => {
    const result = await action.run(() => source.restore(revisionNumber, false));
    if (result) {
      state.reload();
      onChanged();
    }
  };

  if (state.loading && !state.data) return <Loading label={words.loading} />;

  const revisions = state.data ?? [];

  return (
    <>
      <div className="card-header">
        <h3>{words.heading}</h3>
        <span className="faint">{words.count(revisions.length)}</span>
      </div>

      {state.error && <ErrorBanner error={state.error} onRetry={state.reload} />}
      {action.error && <ErrorBanner error={action.error} onDismiss={action.clearError} />}

      {revisions.length === 0 && <p className="muted">{words.empty}</p>}

      <ul className="revision-list">
        {revisions.map((revision) => {
          const isAheadOfLive =
            currentRevisionNumber !== null && revision.revisionNumber > currentRevisionNumber;

          return (
            <li key={revision.revisionNumber}>
              <span className="revision-number">#{revision.revisionNumber}</span>

              <div className="revision-main">
                <strong>{revision.title}</strong>
                <div className="faint">
                  <DateTime value={revision.createdAtUtc} /> · {revision.createdBy}
                  {' · '}
                  {words.characters(revision.contentLength)}
                </div>
                {revision.changeNote && (
                  <p className="faint" style={{ margin: '2px 0 0' }}>
                    {revision.changeNote}
                  </p>
                )}
                <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                  {revision.isCurrent && <span className="badge badge-live">{shared.live}</span>}
                  {isAheadOfLive && (
                    <span className="badge badge-draft" title={words.aheadOfLive}>
                      {shared.draft}
                    </span>
                  )}
                </div>

                <div className="button-row" style={{ marginTop: 6 }}>
                  <button
                    type="button"
                    className="button button-small button-quiet"
                    onClick={() => setViewing(revision)}
                  >
                    {words.view}
                  </button>
                  {currentRevisionNumber !== null && !revision.isCurrent && (
                    <button
                      type="button"
                      className="button button-small button-quiet"
                      onClick={() => setComparing(revision)}
                    >
                      {words.compare}
                    </button>
                  )}
                  {!revision.isCurrent && (
                    <button
                      type="button"
                      className="button button-small"
                      onClick={() => void promote(revision.revisionNumber)}
                      disabled={action.running}
                      title={words.promoteTitle}
                    >
                      {words.promote}
                    </button>
                  )}
                  {!revision.isCurrent && (
                    <button
                      type="button"
                      className="button button-small button-quiet"
                      onClick={() => void restore(revision.revisionNumber)}
                      disabled={action.running}
                      title={words.restoreTitle}
                    >
                      {words.restore}
                    </button>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {viewing && (
        <RevisionViewer source={source} revision={viewing} onClose={() => setViewing(null)} />
      )}

      {comparing && currentRevisionNumber !== null && (
        <RevisionDiff
          source={source}
          left={currentRevisionNumber}
          right={comparing.revisionNumber}
          onClose={() => setComparing(null)}
        />
      )}
    </>
  );
}

function RevisionViewer({
  source,
  revision,
  onClose,
}: {
  source: RevisionHistorySource;
  revision: RevisionRow;
  onClose: () => void;
}): JSX.Element {
  const t = useMessages();
  const state = useAsync(
    () => source.get(revision.revisionNumber),
    [source.key, revision.revisionNumber],
  );

  return (
    <Modal
      open
      wide
      title={t.admin.articles.history.viewerTitle(revision.revisionNumber, revision.title)}
      onClose={onClose}
      footer={
        <button type="button" className="button" onClick={onClose}>
          {t.common.close}
        </button>
      }
    >
      {state.loading && !state.data && <Loading />}
      {state.error && <ErrorBanner error={state.error} onRetry={state.reload} />}
      {state.data && (
        <pre
          style={{
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            margin: 0,
            fontSize: '0.8rem',
          }}
        >
          {state.data.content}
        </pre>
      )}
    </Modal>
  );
}

function RevisionDiff({
  source,
  left,
  right,
  onClose,
}: {
  source: RevisionHistorySource;
  left: number;
  right: number;
  onClose: () => void;
}): JSX.Element {
  const t = useMessages();
  const state = useAsync(
    () => Promise.all([source.get(left), source.get(right)]),
    [source.key, left, right],
  );

  return (
    <Modal
      open
      wide
      title={t.admin.articles.history.diffTitle(left, right)}
      onClose={onClose}
      footer={
        <button type="button" className="button" onClick={onClose}>
          {t.common.close}
        </button>
      }
    >
      {state.loading && !state.data && <Loading />}
      {state.error && <ErrorBanner error={state.error} onRetry={state.reload} />}
      {state.data && <DiffView left={state.data[0]} right={state.data[1]} />}
    </Modal>
  );
}

function DiffView({
  left,
  right,
}: {
  left: RevisionWithContent;
  right: RevisionWithContent;
}): JSX.Element {
  const words = useMessages().admin.articles.history;
  const result = diffLines(left.content, right.content);
  const rows = collapseUnchanged(result.lines, 3);

  return (
    <>
      <p className="faint">
        {words.diffSummary(result.summary.added, result.summary.removed, result.summary.approximate)}
      </p>

      {left.title !== right.title && (
        <div className="notice notice-info">
          <div className="notice-body">
            {richText(words.titleDiffers, {
              from: <strong>{left.title}</strong>,
              to: <strong>{right.title}</strong>,
            })}
          </div>
        </div>
      )}

      <div className="diff">
        {rows.map((row, index) => {
          if (row === 'gap') {
            return (
              <div className="diff-gap" key={`gap-${index}`}>
                ⋯
              </div>
            );
          }

          return (
            <div
              key={`${row.op}-${row.leftLine ?? 'x'}-${row.rightLine ?? 'x'}-${index}`}
              className={`diff-line${
                row.op === 'insert' ? ' diff-insert' : row.op === 'delete' ? ' diff-delete' : ''
              }`}
            >
              <span className="diff-gutter">
                {row.leftLine ?? ''} {row.rightLine ?? ''}
              </span>
              <span>
                {row.op === 'insert' ? '+ ' : row.op === 'delete' ? '- ' : '  '}
                {row.text || ' '}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
}
